import React, { useEffect, useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  X, Loader2, MapPin, DollarSign, TrendingUp, ShieldCheck, FileText,
  Users, CheckCircle, AlertCircle, Upload, Camera, Building2, Clock,
  ChevronRight, Home, ArrowRight, Image, Download, MessageSquare, Send,
  CalendarDays, CircleCheck, Circle, Pencil, Save, RotateCcw, Sparkles
} from "lucide-react";
import { GoldButton } from "./shared/UIComponents";
import { toast } from "sonner";
import DealRoomChat from "./DealRoomChat";
import { fmtUSD } from "../utils/currency";
import { formatAddress, estimatedRiskTier, displayValue } from "../utils/addressFormat";
import { downloadReport } from "../utils/downloadReport";
import { ConditionsPanel, ApprovalsPanel } from "./admin/ConditionsApprovalsPanel";
import TimelinePanel from "./admin/TimelinePanel";
import { TermSheetComparison } from "./dashboard/TermSheetComparison";
import LenderOutreachWorkflow from "./admin/LenderOutreachWorkflow";
import ClosingChecklist from "./ClosingChecklist";
import DealMapCard from "./shared/DealMapCard";
import { SectionHeader, DotLeaderRow, DotLeaderTable, StatusBadge, ScoreBadge, MapThumbnail, MetricPill } from "./shared/DesignSystem";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOAN_LABELS = { fix_flip: "Fix & Flip", bridge: "Bridge", dscr: "DSCR", cash_out: "Cash-Out Refi", construction: "Construction", ground_up: "Ground Up", multifamily: "Multifamily", commercial: "Commercial" };
const STAGE_LABELS = {
  new_lead: "New Lead", intake_complete: "Intake Complete", docs_requested: "Docs Requested",
  underwriting: "Underwriting", matched: "Matched", submitted_to_lender: "Submitted",
  conditional_approval: "Conditional", clear_to_close: "Clear to Close", funded: "Funded", declined: "Declined",
};
const STAGE_COLORS = {
  new_lead: "bg-slate-100 text-slate-600", intake_complete: "bg-blue-100 text-blue-600",
  docs_requested: "bg-amber-100 text-amber-600", underwriting: "bg-purple-100 text-purple-600",
  matched: "bg-emerald-100 text-emerald-600", submitted_to_lender: "bg-cyan-100 text-cyan-600",
  conditional_approval: "bg-teal-100 text-teal-600", clear_to_close: "bg-lime-100 text-lime-600",
  funded: "bg-green-100 text-green-600", declined: "bg-red-100 text-red-600",
};

export default function DealDetailModal({ leadId, onClose, viewerRole }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [tab, setTab] = useState("overview");
  const [uploading, setUploading] = useState(false);
  const [editingFees, setEditingFees] = useState(false);
  const [feeForm, setFeeForm] = useState({});
  const [savingFees, setSavingFees] = useState(false);
  const [adminNotes, setAdminNotes] = useState("");
  const [adminRiskOverride, setAdminRiskOverride] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [editingPackage, setEditingPackage] = useState(false);
  const [packageForm, setPackageForm] = useState({ summary: "", strengths: [], risks: [] });
  const [savingPackage, setSavingPackage] = useState(false);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiSummaryLoading, setAiSummaryLoading] = useState(false);
  const [lenderBrief, setLenderBrief] = useState(null);
  const fileRef = useRef(null);

  const fetchDeal = useCallback(() => {
    if (!leadId) return;
    axios.get(`${API}/deals/${leadId}/detail`).then(r => setDetail(r.data)).catch(console.error);
  }, [leadId]);

  useEffect(() => {
    if (!leadId) return;
    setLoading(true);
    setLoadError(false);
    setTab("overview");
    axios.get(`${API}/deals/${leadId}/detail`)
      .then(r => {
        setDetail(r.data);
        setAdminNotes(r.data.admin_notes || "");
        setAdminRiskOverride(r.data.admin_risk_override ?? "");
        if (r.data.deal_package) {
          setPackageForm({
            summary: r.data.deal_package.summary || "",
            strengths: r.data.deal_package.strengths || [],
            risks: r.data.deal_package.risks || [],
          });
        }
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [leadId]);

  // Fetch existing AI underwriting summary when switching to underwriting tab
  useEffect(() => {
    if (tab !== "underwriting" || !leadId) return;
    const token = localStorage.getItem("ic_token");
    if (!token) return;
    axios.get(`${API}/underwriting/deal/${leadId}/ai-summary`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (r.data.summary) setAiSummary(r.data.summary); })
      .catch(() => {});
  }, [tab, leadId]);

  // Fetch lender brief for lender-facing view (shows between KPI strip and Deal Lifecycle)
  useEffect(() => {
    if (!leadId || !detail) return;
    const role = detail?.viewer_role;
    if (role !== "lender" && role !== "investor") return;
    const token = localStorage.getItem("ic_token");
    if (!token) return;
    axios.get(`${API}/underwriting/deal/${leadId}/lender-brief`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (r.data.lender_brief) setLenderBrief(r.data.lender_brief);
      })
      .catch(() => {});
  }, [leadId, detail]);

  const generateAiSummary = async () => {
    setAiSummaryLoading(true);
    try {
      const token = localStorage.getItem("ic_token");
      const res = await axios.post(`${API}/underwriting/deal/${leadId}/ai-summary`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setAiSummary(res.data.summary);
    } catch (err) { console.error(err); }
    finally { setAiSummaryLoading(false); }
  };

  const handlePhotoUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);
      try {
        await axios.post(`${API}/documents/upload?lead_id=${leadId}&doc_type=property_photo`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } catch (err) { console.error(err); }
    }
    // Refresh detail
    const r = await axios.get(`${API}/deals/${leadId}/detail`);
    setDetail(r.data);
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const saveAdminNotes = async () => {
    setSavingNotes(true);
    try {
      await axios.put(`${API}/deals/${leadId}/admin-notes`, {
        admin_notes: adminNotes,
        admin_risk_override: adminRiskOverride ? parseInt(adminRiskOverride) : null,
      });
      fetchDeal();
    } catch (err) { console.error(err); }
    setSavingNotes(false);
  };

  const saveDealPackage = async () => {
    setSavingPackage(true);
    try {
      await axios.put(`${API}/deals/${leadId}/deal-package`, packageForm);
      fetchDeal();
      setEditingPackage(false);
    } catch (err) { console.error(err); }
    setSavingPackage(false);
  };

  if (!leadId) return null;

  const d = detail;
  const isAdmin = d?.viewer_role === "admin";
  const isOwner = d?.is_owner;
  const isLender = d?.viewer_role === "lender" || d?.viewer_role === "investor";

  const tabs = ["overview"];
  if (d?.underwriting) tabs.push("underwriting");
  if (d?.deal_package?.summary) tabs.push("package");
  if ((d?.term_sheets?.length || 0) > 0 || d?.term_sheet_count > 0) tabs.push("offers");
  tabs.push("photos");
  if ((d?.documents?.length || 0) > 0) tabs.push("documents");
  if (isAdmin && d?.fees) tabs.push("fees");
  if (!isAdmin && ["originations", "underwriter", "processor", "lender_relations", "investor_relations"].includes(viewerRole) && d?.fees) tabs.push("fees");
  tabs.push("messages");
  if (isAdmin || ["originations", "underwriter", "processor", "lender_relations", "investor_relations"].includes(viewerRole)) {
    tabs.push("conditions");
    tabs.push("approval");
    tabs.push("timeline");
  }
  if (isAdmin || viewerRole === "lender" || viewerRole === "investor") tabs.push("reports");
  if (isAdmin || ["originations", "lender_relations"].includes(viewerRole)) tabs.push("lender_workflow");
  if (["terms", "funding"].includes(d?.stage)) tabs.push("closing");

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
        onClick={onClose} data-testid="deal-detail-overlay">
        <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
          onClick={e => e.stopPropagation()} data-testid="deal-detail-modal"
          className="relative w-full max-w-4xl rounded-3xl border border-cream-warm bg-white shadow-2xl max-h-[90vh] overflow-hidden flex flex-col mx-3 sm:mx-0">

          {loading ? (
            <div className="flex items-center justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>
          ) : loadError || !d ? (
            <div className="flex flex-col items-center justify-center py-24 px-6 text-center" data-testid="deal-detail-error">
              <AlertCircle className="h-10 w-10 text-red-400 mb-3" />
              <div className="text-sm font-semibold text-[#333] mb-1">Failed to load deal details</div>
              <div className="text-xs text-[#888] mb-4">This deal may have incomplete data or a server error occurred.</div>
              <div className="flex gap-2">
                <button onClick={() => { setLoading(true); setLoadError(false); axios.get(`${API}/deals/${leadId}/detail`).then(r => setDetail(r.data)).catch(() => setLoadError(true)).finally(() => setLoading(false)); }}
                  className="px-4 py-2 rounded-lg text-xs font-semibold bg-emerald-700 text-white hover:bg-emerald-600 transition-colors"
                  data-testid="deal-detail-retry">Retry</button>
                <button onClick={onClose}
                  className="px-4 py-2 rounded-lg text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                  data-testid="deal-detail-close-error">Close</button>
              </div>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="shrink-0 bg-gradient-to-r from-emerald-800 to-emerald-900 px-4 sm:px-6 py-4 sm:py-5 text-white">
                <button onClick={onClose} className="absolute top-4 right-4 p-2 text-white/60 hover:text-white z-10" data-testid="deal-detail-close">
                  <X className="h-5 w-5" />
                </button>
                <div className="flex items-center gap-3 mb-3">
                  {d.deal_number && (
                    <span className="px-3 py-1 rounded-full text-xs font-mono font-bold bg-gold/20 text-gold border border-gold/30" data-testid="deal-number-badge">
                      {d.deal_number}
                    </span>
                  )}
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${STAGE_COLORS[d.stage] || "bg-slate-100 text-slate-600"}`}>
                    {STAGE_LABELS[d.stage] || d.stage}
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-white/10 text-white/90 border border-white/20">
                    {d.loan_type_label}
                  </span>
                </div>
                <h2 className="text-xl font-bold text-white mb-1" data-testid="deal-detail-address">
                  {formatAddress(d.property?.address) || "Deal Details"}
                </h2>
                <div className="flex items-center gap-4 text-sm text-white/60">
                  {d.property?.state && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{d.property.state}</span>}
                  <span className="flex items-center gap-1"><DollarSign className="h-3.5 w-3.5" />{fmtUSD(d.financing?.requested_loan_amount || 0)}</span>
                  {d.underwriting?.risk_score && (
                    <span className={`flex items-center gap-1 ${(d.underwriting.risk_score || 0) <= 30 ? "text-emerald-400" : (d.underwriting.risk_score || 0) <= 60 ? "text-amber-400" : "text-red-400"}`}>
                      <ShieldCheck className="h-3.5 w-3.5" />Risk: {d.underwriting.risk_score}
                    </span>
                  )}
                  {d.matched_lenders_count > 0 && (
                    <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{d.matched_lenders_count} lenders</span>
                  )}
                </div>
              {/* Map Thumbnail */}
              {d.property?.address && (
                <div className="absolute right-4 top-4 w-36 opacity-80 hover:opacity-100 transition-opacity" data-testid="deal-modal-map">
                  <DealMapCard address={formatAddress(d.property.address)} size="sm" />
                </div>
              )}
              {/* Deal Visibility Control (Admin Only - Task 7) */}
              {isAdmin && (
                <DealVisibilityControl leadId={leadId} initialVisibility={d.visibility} />
              )}
              </div>

              {/* Tabs */}
              <div className="shrink-0 px-4 sm:px-6 pt-4 flex gap-1.5 flex-wrap border-b border-cream-warm pb-3">
                {tabs.map(t => (
                  <button key={t} onClick={() => setTab(t)} data-testid={`detail-tab-${t}`}
                    className={`px-3.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      tab === t ? "bg-emerald-700 text-white" : "bg-cream text-[#555] hover:bg-cream-warm border border-cream-warm"
                    }`}>{t === "offers" ? "Term Sheets" : t === "package" ? "Deal Package" : t === "messages" ? "Messages" : t === "conditions" ? "Conditions" : t === "approval" ? "Approval" : t === "timeline" ? "Timeline" : t === "lender_workflow" ? "Lender Outreach" : t.charAt(0).toUpperCase() + t.slice(1)}</button>
                ))}
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5">
                {/* Overview Tab */}
                {tab === "overview" && (
                  <div className="space-y-6" data-testid="detail-overview">
                    {/* Two-column layout */}
                    <div style={{ display: "grid", gridTemplateColumns: "38% 1fr", gap: 24 }} className="detail-overview-grid">

                      {/* LEFT COLUMN */}
                      <div>
                        <div style={{ borderRadius: "var(--ic-radius-lg)", overflow: "hidden", border: "1px solid var(--ic-border)", marginBottom: 16 }}>
                          <MapThumbnail address={formatAddress(d.property?.address)} height={200} />
                        </div>
                        <div style={{ marginBottom: 8 }}>
                          <StatusBadge status={STAGE_LABELS[d.stage] || d.stage} />
                        </div>
                        {d.created_at && (
                          <div style={{ fontSize: 12, color: "var(--ic-text-secondary)", marginBottom: 8 }}>
                            Posted {new Date(d.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                          </div>
                        )}
                        <div style={{ display: "inline-flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                          <span style={{ padding: "3px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600, background: "var(--ic-green-dark)", color: "var(--ic-text-inverse)" }}>
                            {d.loan_type_label || LOAN_LABELS[d.loan_type] || "—"}
                          </span>
                        </div>
                        {d.underwriting?.risk_score > 0 && (
                          <div style={{ marginTop: 4 }}>
                            <ScoreBadge score={d.underwriting.risk_score} />
                          </div>
                        )}
                        {/* Borrower Avatar */}
                        {d.borrower?.full_name && (
                          <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{ width: 40, height: 40, borderRadius: "50%", background: "var(--ic-green-dark)", color: "var(--ic-text-inverse)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700 }}>
                              {d.borrower.full_name.split(" ").map(w => w[0]).join("").substring(0, 2).toUpperCase()}
                            </div>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ic-text-primary)" }}>{isLender && !d.lender_access_granted ? "Undisclosed" : d.borrower.full_name}</div>
                              <div style={{ fontSize: 11, color: "var(--ic-text-secondary)" }}>Borrower</div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* RIGHT COLUMN */}
                      <div>
                        {/* SECTION 1: LOAN INFO */}
                        <SectionHeader title="Loan Info" />
                        <DotLeaderRow label="Property Address" value={formatAddress(d.property?.address) || "—"} />
                        <DotLeaderRow label="Deal ID" value={d.deal_number || `Deal ${leadId?.substring(0, 8)}`} />
                        <DotLeaderRow label="Loan Type" value={d.loan_type_label || LOAN_LABELS[d.loan_type] || "—"} />
                        <DotLeaderRow label="Sponsor" value={isLender && !d.lender_access_granted ? "Undisclosed" : (d.borrower?.full_name || "—")} />
                        <DotLeaderRow label="Status" value={STAGE_LABELS[d.stage] || d.stage || "—"} />
                        {d.created_at && <DotLeaderRow label="Date Posted" value={new Date(d.created_at).toLocaleDateString("en-US")} />}

                        {/* SECTION 2: LOAN DETAILS */}
                        <div style={{ marginTop: 24 }}>
                          <SectionHeader title="Loan Details" />
                          <DotLeaderRow label="Loan Requested" value={d.financing?.requested_loan_amount ? fmtUSD(d.financing.requested_loan_amount) : "—"} bold />
                          <DotLeaderRow label="Purchase Price" value={d.property?.purchase_price ? fmtUSD(d.property.purchase_price) : "—"} />
                          <DotLeaderRow label="ARV" value={d.property?.arv ? fmtUSD(d.property.arv) : "—"} />
                          {d.financing?.ltv != null && <DotLeaderRow label="LTV" value={`${(d.financing.ltv * 100).toFixed(1)}%`} />}
                          {d.financing?.ltc != null && <DotLeaderRow label="LTC" value={`${(d.financing.ltc * 100).toFixed(1)}%`} />}
                          {d.financing?.term_months && <DotLeaderRow label="Term" value={`${d.financing.term_months} months`} />}
                          {d.financing?.close_timeline_days && <DotLeaderRow label="Close Timeline" value={`${d.financing.close_timeline_days} days`} />}
                          {d.financing?.rate_min != null && <DotLeaderRow label="Interest Rate" value={d.financing.rate_max ? `${d.financing.rate_min}% – ${d.financing.rate_max}%` : `${d.financing.rate_min}%`} />}
                        </div>

                        {/* Valuation extras */}
                        {(d.property?.tax_assessed || d.property?.zestimate || d.property?.sqft) && (
                          <div style={{ marginTop: 24 }}>
                            <SectionHeader title="Property Details" />
                            {d.property.property_type && <DotLeaderRow label="Type" value={d.property.property_type === "sfr" ? "Single Family" : d.property.property_type} />}
                            {d.property.sqft && <DotLeaderRow label="Sq Ft" value={d.property.sqft.toLocaleString()} />}
                            {d.property.bedrooms && <DotLeaderRow label="Bedrooms" value={d.property.bedrooms} />}
                            {d.property.bathrooms && <DotLeaderRow label="Bathrooms" value={d.property.bathrooms} />}
                            {d.property.year_built && <DotLeaderRow label="Year Built" value={d.property.year_built} />}
                            {d.property.tax_assessed && <DotLeaderRow label="Tax Assessed" value={fmtUSD(d.property.tax_assessed)} />}
                            {d.property.zestimate && <DotLeaderRow label="Zestimate" value={fmtUSD(d.property.zestimate)} />}
                            {d.property.rehab_budget && <DotLeaderRow label="Rehab Budget" value={fmtUSD(d.property.rehab_budget)} />}
                          </div>
                        )}

                        {/* SECTION 3: QUOTES FOR THIS DEAL */}
                        <div style={{ marginTop: 24 }}>
                          <SectionHeader title="Quotes for This Deal" />
                          <DotLeaderTable
                            columns={[
                              { key: "lender_name", label: "Lender" },
                              { key: "amount", label: "Amount", render: (r) => r.loan_amount ? fmtUSD(r.loan_amount) : "—" },
                              { key: "rate", label: "Rate", render: (r) => r.interest_rate ? `${r.interest_rate}%` : "—" },
                              { key: "term", label: "Term", render: (r) => r.term_months ? `${r.term_months} mo` : "—" },
                              { key: "orig_fee", label: "Orig. Fee", render: (r) => r.origination_fee ? fmtUSD(r.origination_fee) : (r.points ? `${r.points} pts` : "—") },
                              { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status || "submitted"} /> },
                              { key: "action", label: "Action", render: (r) => (
                                <button className="ic-btn-outline" style={{ padding: "4px 12px", fontSize: 12, flex: "none" }}>VIEW</button>
                              )},
                            ]}
                            rows={(d.term_sheets || []).map((ts, i) => ({
                              ...ts,
                              lender_name: isLender && !isAdmin ? "You" : (ts.lender_name || ts.lender_company || `Lender #${i + 1}`),
                            }))}
                            emptyText="No quotes submitted yet. Be the first to quote this deal."
                          />
                        </div>
                      </div>
                    </div>

                    {/* AI Lender Brief */}
                    {isLender && lenderBrief && (
                      <div style={{ borderRadius: "var(--ic-radius-lg)", border: "2px solid var(--ic-gold)", background: "var(--ic-bg-card)", padding: 20 }} data-testid="lender-brief-section">
                        <SectionHeader title="AI Deal Brief" />
                        {lenderBrief.headline && (
                          <p style={{ fontSize: 14, fontWeight: 600, color: "var(--ic-text-primary)", marginBottom: 8 }}>{lenderBrief.headline}</p>
                        )}
                        {lenderBrief.opportunity_summary && (
                          <p style={{ fontSize: 13, color: "var(--ic-text-secondary)", lineHeight: 1.6, marginBottom: 12 }}>{lenderBrief.opportunity_summary}</p>
                        )}
                        {lenderBrief.key_highlights?.length > 0 && (
                          <div style={{ marginBottom: 12 }}>
                            {lenderBrief.key_highlights.map((h, i) => (
                              <div key={i} style={{ fontSize: 13, color: "var(--ic-green-dark)", padding: "4px 0", borderBottom: "1px dotted var(--ic-border)", display: "flex", alignItems: "center", gap: 6 }}>
                                <CheckCircle className="h-3 w-3" style={{ color: "var(--ic-green-dark)" }} />
                                {typeof h === 'string' ? h : <span><span style={{ fontWeight: 600 }}>{h.label}:</span> {h.value}</span>}
                              </div>
                            ))}
                          </div>
                        )}
                        {lenderBrief.considerations?.length > 0 && (
                          <div>
                            <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--ic-status-pending)", marginBottom: 6 }}>Considerations</div>
                            {lenderBrief.considerations.map((c, i) => (
                              <div key={i} style={{ fontSize: 12, color: "var(--ic-text-secondary)", padding: "3px 0" }}>
                                {typeof c === "string" ? c : c.point || JSON.stringify(c)}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Responsive */}
                    <style>{`
                      @media (max-width: 768px) {
                        .detail-overview-grid { grid-template-columns: 1fr !important; }
                      }
                    `}</style>

                    {/* Financing Request */}
                    <div className="rounded-xl border border-cream-warm bg-cream p-4">
                      <h4 className="text-sm font-bold text-[#0A0A0A] mb-3 uppercase tracking-wider">Financing Request</h4>
                      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                        <Row label="Loan Type" value={d.loan_type_label || null} />
                        <Row label="Requested Amount" value={d.financing?.requested_loan_amount ? fmtUSD(d.financing.requested_loan_amount) : null} />
                        <Row label="Exit Strategy" value={d.financing?.exit_strategy || null} />
                        <Row label="Close Timeline" value={d.financing?.close_timeline ? `${d.financing.close_timeline} days` : null} />
                      </div>
                    </div>

                    {/* Photo thumbnails preview */}
                    {d.photos?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-bold text-[#0A0A0A] mb-3 uppercase tracking-wider flex items-center gap-2"><Camera className="h-4 w-4 text-gold-dark" /> Property Photos</h4>
                        <div className="flex gap-2 overflow-x-auto pb-2">
                          {d.photos.slice(0, 6).map(p => (
                            <div key={p.doc_id} className="shrink-0 w-24 h-24 rounded-xl border border-cream-warm overflow-hidden bg-cream">
                              <img src={`${API}/documents/download/${p.doc_id}?auth=${localStorage.getItem("ic_token")}`} alt={p.filename} className="w-full h-full object-cover" />
                            </div>
                          ))}
                          {d.photos.length > 6 && (
                            <button onClick={() => setTab("photos")} className="shrink-0 w-24 h-24 rounded-xl border border-cream-warm bg-cream flex items-center justify-center text-xs text-[#888]">
                              +{d.photos.length - 6} more
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Deal Context Strip (Admin) */}
                    {isAdmin && d.deal_context && (
                      <div className="rounded-xl border border-cream-warm bg-cream p-4" data-testid="deal-context">
                        <h4 className="text-sm font-bold text-[#0A0A0A] mb-3 uppercase tracking-wider flex items-center gap-2">
                          <Clock className="h-4 w-4 text-[#999]" /> Deal Timeline
                        </h4>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          <div className="text-center">
                            <div className="text-[10px] uppercase tracking-wider text-[#999]">Deal Age</div>
                            <div className="text-lg font-bold text-[#0A0A0A]">{d.deal_context.deal_age_days ?? "—"}d</div>
                          </div>
                          <div className="text-center">
                            <div className="text-[10px] uppercase tracking-wider text-[#999]">Days in Stage</div>
                            <div className="text-lg font-bold text-amber-700">{d.deal_context.days_in_stage ?? "—"}d</div>
                          </div>
                          <div className="text-center">
                            <div className="text-[10px] uppercase tracking-wider text-[#999]">Submitted</div>
                            <div className="text-xs font-semibold text-[#555]">
                              {d.deal_context.created_at ? new Date(d.deal_context.created_at).toLocaleDateString() : "—"}
                            </div>
                          </div>
                          <div className="text-center">
                            <div className="text-[10px] uppercase tracking-wider text-[#999]">Last Activity</div>
                            <div className="text-xs font-semibold text-[#555]">
                              {d.deal_context.last_activity_at ? new Date(d.deal_context.last_activity_at).toLocaleDateString() : "—"}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Pre-Matching Readiness Checklist (Admin) */}
                    {isAdmin && d.pre_matching_readiness && (
                      <div className="rounded-xl border border-cream-warm bg-cream p-4" data-testid="pre-matching-readiness">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider flex items-center gap-2">
                            <ShieldCheck className="h-4 w-4 text-emerald-600" /> Pre-Matching Readiness
                          </h4>
                          <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                            d.pre_matching_readiness.pct === 100 ? "bg-emerald-100 text-emerald-700" :
                            d.pre_matching_readiness.pct >= 60 ? "bg-amber-100 text-amber-700" :
                            "bg-red-100 text-red-600"
                          }`} data-testid="readiness-pct">
                            {d.pre_matching_readiness.ready_count}/{d.pre_matching_readiness.total_count} Ready ({d.pre_matching_readiness.pct}%)
                          </span>
                        </div>
                        <div className="w-full h-2 bg-[#e8e0d0] rounded-full mb-3 overflow-hidden">
                          <div className={`h-full rounded-full transition-all ${
                            d.pre_matching_readiness.pct === 100 ? "bg-emerald-500" :
                            d.pre_matching_readiness.pct >= 60 ? "bg-amber-500" : "bg-red-400"
                          }`} style={{ width: `${d.pre_matching_readiness.pct}%` }} />
                        </div>
                        <div className="grid sm:grid-cols-2 gap-2">
                          {d.pre_matching_readiness.checklist.map(item => (
                            <div key={item.key} className={`flex items-start gap-2 px-3 py-2 rounded-lg text-sm ${
                              item.ready ? "bg-emerald-50 border border-emerald-100" : "bg-orange-50 border border-orange-100"
                            }`} data-testid={`readiness-${item.key}`}>
                              {item.ready
                                ? <CircleCheck className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
                                : <Circle className="h-4 w-4 text-orange-400 shrink-0 mt-0.5" />
                              }
                              <div>
                                <div className={`text-xs font-semibold ${item.ready ? "text-emerald-700" : "text-orange-700"}`}>{item.label}</div>
                                <div className="text-[10px] text-[#888]">{item.detail}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Underwriting Tab */}
                {tab === "underwriting" && d.underwriting && (
                  <div className="space-y-5" data-testid="detail-underwriting">
                    {/* Top Metrics Row */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div className="rounded-xl border border-cream-warm bg-cream p-4 text-center">
                        <div className="text-[10px] uppercase tracking-wider text-[#999]">Risk Score</div>
                        <div className={`text-3xl font-bold ${(d.underwriting.risk_score || 0) <= 30 ? "text-emerald-600" : (d.underwriting.risk_score || 0) <= 60 ? "text-amber-600" : "text-red-600"}`}>
                          {d.underwriting.risk_score ?? "Pending"}
                        </div>
                        <div className="text-xs text-[#888] capitalize">{d.underwriting.risk_band || "pending"} risk</div>
                      </div>
                      <div className="rounded-xl border border-cream-warm bg-cream p-4 text-center">
                        <div className="text-[10px] uppercase tracking-wider text-[#999]">Decision</div>
                        <div className={`text-lg font-bold capitalize ${d.underwriting.decision === "prequalify" ? "text-emerald-600" : "text-gold-dark"}`}>
                          {d.underwriting.decision?.replace("_", " ") || "Pending"}
                        </div>
                      </div>
                      <div className="rounded-xl border border-cream-warm bg-cream p-4 text-center">
                        <div className="text-[10px] uppercase tracking-wider text-[#999]">Max Loan</div>
                        <div className="text-lg font-bold text-[#0A0A0A]">{fmtUSD(d.underwriting.recommended_max_loan) || "Pending"}</div>
                      </div>
                      <div className="rounded-xl border border-cream-warm bg-cream p-4 text-center">
                        <div className="text-[10px] uppercase tracking-wider text-[#999]">Matched Lenders</div>
                        <div className="text-lg font-bold text-[#0A0A0A]">{d.matched_lenders_count ?? 0}</div>
                      </div>
                    </div>

                    {/* Two-Column Layout: Analysis + Leverage */}
                    <div className="grid lg:grid-cols-2 gap-4">
                      {/* Underwriting Analysis */}
                      {d.underwriting.reasons?.length > 0 && (
                        <div className="rounded-xl border border-cream-warm bg-cream p-4">
                          <h4 className="text-xs uppercase tracking-wider text-[#999] mb-3 flex items-center gap-2">
                            <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> AI Analysis
                          </h4>
                          <ul className="space-y-2">
                            {d.underwriting.reasons.map((r, i) => (
                              <li key={i} className="text-sm text-[#333] flex items-start gap-2">
                                <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" /> {r}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Leverage Metrics */}
                      <div className="rounded-xl border border-cream-warm bg-cream p-4">
                        <h4 className="text-xs uppercase tracking-wider text-[#999] mb-3 flex items-center gap-2">
                          <TrendingUp className="h-3.5 w-3.5 text-blue-600" /> Leverage Metrics
                        </h4>
                        <div className="space-y-3">
                          {d.financing?.ltv != null && (
                            <div>
                              <div className="flex justify-between text-sm mb-1">
                                <span className="text-[#888]">LTV (Loan-to-Value)</span>
                                <span className={`font-bold ${d.financing.ltv > 0.75 ? "text-red-600" : d.financing.ltv > 0.65 ? "text-amber-600" : "text-emerald-600"}`}>
                                  {(d.financing.ltv * 100).toFixed(1)}%
                                </span>
                              </div>
                              <div className="w-full h-2 bg-[#e8e0d0] rounded-full overflow-hidden">
                                <div className={`h-full rounded-full ${d.financing.ltv > 0.75 ? "bg-red-400" : d.financing.ltv > 0.65 ? "bg-amber-400" : "bg-emerald-400"}`}
                                  style={{ width: `${Math.min(d.financing.ltv * 100, 100)}%` }} />
                              </div>
                            </div>
                          )}
                          {d.financing?.ltc != null && (
                            <div>
                              <div className="flex justify-between text-sm mb-1">
                                <span className="text-[#888]">LTC (Loan-to-Cost)</span>
                                <span className={`font-bold ${d.financing.ltc > 0.85 ? "text-red-600" : d.financing.ltc > 0.75 ? "text-amber-600" : "text-emerald-600"}`}>
                                  {(d.financing.ltc * 100).toFixed(1)}%
                                </span>
                              </div>
                              <div className="w-full h-2 bg-[#e8e0d0] rounded-full overflow-hidden">
                                <div className={`h-full rounded-full ${d.financing.ltc > 0.85 ? "bg-red-400" : d.financing.ltc > 0.75 ? "bg-amber-400" : "bg-emerald-400"}`}
                                  style={{ width: `${Math.min(d.financing.ltc * 100, 100)}%` }} />
                              </div>
                            </div>
                          )}
                          <div className="grid grid-cols-2 gap-3 pt-2 border-t border-cream-warm">
                            <div className="text-center">
                              <div className="text-[10px] uppercase tracking-wider text-[#999]">Purchase</div>
                              <div className="text-sm font-bold text-[#0A0A0A]">{fmtUSD(d.property?.purchase_price || 0)}</div>
                            </div>
                            <div className="text-center">
                              <div className="text-[10px] uppercase tracking-wider text-[#999]">ARV</div>
                              <div className="text-sm font-bold text-[#0A0A0A]">{fmtUSD(d.property?.arv || 0)}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* AI Underwriting Summary */}
                    {isAdmin && (
                      <div className="rounded-xl border border-purple-200 bg-purple-50/30 p-5 space-y-4" data-testid="underwriting-ai-section">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="text-sm font-bold text-[#333] flex items-center gap-2">
                              <Sparkles className="h-4 w-4 text-purple-600" /> AI Underwriting Analysis
                            </h4>
                            <p className="text-[10px] text-[#888] mt-0.5">
                              {aiSummary ? `Generated ${aiSummary.generated_at ? new Date(aiSummary.generated_at).toLocaleDateString() : ""}${aiSummary.generated_by_name ? ` by ${aiSummary.generated_by_name}` : ""}` : "Use AI to generate a comprehensive underwriting analysis"}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={generateAiSummary}
                              disabled={aiSummaryLoading}
                              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold bg-purple-600 text-white hover:bg-purple-500 disabled:opacity-50 transition-colors"
                              data-testid="generate-ai-summary-btn"
                            >
                              {aiSummaryLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                              {aiSummary ? "Regenerate" : "AI Populate"}
                            </button>
                            <button
                              onClick={() => downloadReport(leadId, "underwriting")}
                              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold border border-[#e8e0d0] bg-white text-[#555] hover:bg-[#faf7f2] transition-colors"
                              data-testid="generate-underwriting-pdf"
                            >
                              <Download className="h-3.5 w-3.5" /> PDF
                            </button>
                          </div>
                        </div>

                        {/* AI Summary Content */}
                        {aiSummaryLoading && !aiSummary && (
                          <div className="flex items-center justify-center py-8 text-sm text-purple-600 gap-2">
                            <Loader2 className="h-5 w-5 animate-spin" /> Analyzing deal data...
                          </div>
                        )}

                        {aiSummary && (
                          <div className="space-y-4" data-testid="ai-summary-content">
                            {/* Executive Summary */}
                            <div className="rounded-lg border border-purple-100 bg-white p-4">
                              <div className="text-[10px] uppercase tracking-wider text-purple-600 font-semibold mb-2">Executive Summary</div>
                              <p className="text-sm text-[#333] leading-relaxed">{aiSummary.executive_summary}</p>
                            </div>

                            {/* Recommendation */}
                            {aiSummary.recommendation && (
                              <div className={`rounded-lg border p-3 flex items-center gap-3 ${
                                aiSummary.recommendation?.toLowerCase().includes("approve") ? "border-emerald-200 bg-emerald-50" :
                                aiSummary.recommendation?.toLowerCase().includes("decline") ? "border-red-200 bg-red-50" :
                                "border-amber-200 bg-amber-50"
                              }`} data-testid="ai-recommendation">
                                <CheckCircle className={`h-5 w-5 shrink-0 ${
                                  aiSummary.recommendation?.toLowerCase().includes("approve") ? "text-emerald-600" :
                                  aiSummary.recommendation?.toLowerCase().includes("decline") ? "text-red-600" :
                                  "text-amber-600"
                                }`} />
                                <div>
                                  <div className="text-[10px] uppercase tracking-wider text-[#999] font-semibold">Recommendation</div>
                                  <div className="text-sm font-medium text-[#333]">{aiSummary.recommendation}</div>
                                </div>
                              </div>
                            )}

                            {/* Strengths & Risks */}
                            <div className="grid lg:grid-cols-2 gap-3">
                              {aiSummary.strengths?.length > 0 && (
                                <div className="rounded-lg border border-emerald-100 bg-emerald-50/30 p-4">
                                  <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold mb-2">Strengths</div>
                                  <ul className="space-y-1.5">
                                    {aiSummary.strengths.map((s, i) => (
                                      <li key={i} className="text-xs text-[#333] flex items-start gap-2">
                                        <CheckCircle className="h-3.5 w-3.5 text-emerald-500 mt-0.5 shrink-0" />
                                        <span>{typeof s === "string" ? s : s.point || JSON.stringify(s)}{typeof s === "object" && s.metric ? <span className="text-[#999] ml-1">({s.metric})</span> : null}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {aiSummary.risks?.length > 0 && (
                                <div className="rounded-lg border border-red-100 bg-red-50/30 p-4">
                                  <div className="text-[10px] uppercase tracking-wider text-red-700 font-semibold mb-2">Risks</div>
                                  <ul className="space-y-1.5">
                                    {aiSummary.risks.map((r, i) => (
                                      <li key={i} className="text-xs text-[#333] flex items-start gap-2">
                                        <AlertCircle className="h-3.5 w-3.5 text-red-500 mt-0.5 shrink-0" />
                                        <span>
                                          {typeof r === "string" ? r : r.point || JSON.stringify(r)}
                                          {typeof r === "object" && r.metric ? <span className="text-[#999] ml-1">({r.metric})</span> : null}
                                          {typeof r === "object" && r.mitigation ? <div className="text-[10px] text-emerald-600 mt-0.5">Mitigation: {r.mitigation}</div> : null}
                                        </span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>

                            {/* Conditions */}
                            {aiSummary.conditions?.length > 0 && (
                              <div className="rounded-lg border border-amber-100 bg-amber-50/30 p-4">
                                <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold mb-2">Prior-to-Close Conditions</div>
                                <ul className="space-y-1.5">
                                  {aiSummary.conditions.map((c, i) => (
                                    <li key={i} className="text-xs text-[#333] flex items-start gap-2">
                                      <Circle className="h-3.5 w-3.5 text-amber-500 mt-0.5 shrink-0" /> {typeof c === "string" ? c : c.condition || JSON.stringify(c)}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Suggested Terms */}
                            {aiSummary.suggested_terms && (
                              <div className="rounded-lg border border-[#e8e0d0] bg-[#faf7f2] p-3">
                                <div className="text-[10px] uppercase tracking-wider text-[#999] font-semibold mb-1">Suggested Terms</div>
                                {typeof aiSummary.suggested_terms === "string" ? (
                                  <p className="text-xs text-[#555]">{aiSummary.suggested_terms}</p>
                                ) : (
                                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                    {Object.entries(aiSummary.suggested_terms).map(([k, v]) => (
                                      <div key={k} className="text-xs">
                                        <span className="text-[#999] capitalize">{k.replace(/_/g, " ")}:</span>{" "}
                                        <span className="font-medium text-[#333]">{typeof v === "string" ? v : JSON.stringify(v)}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}

                            {aiSummary.ai_fallback && (
                              <div className="text-[10px] text-amber-600 flex items-center gap-1">
                                <AlertCircle className="h-3 w-3" /> Generated using rule-based fallback (AI service unavailable)
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Admin Notes & Risk Override */}
                    {isAdmin && (
                      <div className="rounded-xl border border-cream-warm bg-cream p-4" data-testid="admin-notes-section">
                        <h4 className="text-xs uppercase tracking-wider text-[#999] mb-3 flex items-center gap-2">
                          <Pencil className="h-3.5 w-3.5" /> Admin Underwriting Notes
                        </h4>
                        <textarea
                          value={adminNotes}
                          onChange={e => setAdminNotes(e.target.value)}
                          placeholder="Add your underwriting review notes here... (e.g., borrower experience verified, comparable sales checked, concerns about ARV)"
                          className="w-full rounded-lg border border-[#e0d8c8] bg-white p-3 text-sm text-[#333] placeholder:text-[#bbb] focus:outline-none focus:border-emerald-400 min-h-[80px] resize-y"
                          data-testid="admin-notes-input"
                        />
                        <div className="flex items-center gap-4 mt-3">
                          <div className="flex items-center gap-2">
                            <label className="text-[10px] uppercase tracking-wider text-[#999]">Risk Override</label>
                            <input
                              type="number" min="0" max="100" value={adminRiskOverride}
                              onChange={e => setAdminRiskOverride(e.target.value)}
                              placeholder="—"
                              className="w-16 rounded-lg border border-[#e0d8c8] bg-white px-2 py-1.5 text-sm text-center font-bold focus:outline-none focus:border-emerald-400"
                              data-testid="risk-override-input"
                            />
                            <span className="text-[10px] text-[#999]">/ 100</span>
                          </div>
                          <button onClick={saveAdminNotes} disabled={savingNotes}
                            className="ml-auto flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold bg-emerald-700 text-white hover:bg-emerald-600 disabled:opacity-50 transition-colors"
                            data-testid="save-admin-notes">
                            {savingNotes ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                            Save Notes
                          </button>
                        </div>
                        {d.admin_risk_override && (
                          <div className="mt-2 text-[10px] text-amber-600 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" /> Admin risk override active: {d.admin_risk_override}/100 (AI score: {d.underwriting.risk_score})
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Deal Package Tab */}
                {tab === "package" && d.deal_package && (
                  <div className="space-y-5" data-testid="detail-package">
                    {/* Edit toggle for admins */}
                    {isAdmin && (
                      <div className="flex justify-end">
                        <button onClick={() => setEditingPackage(!editingPackage)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                            editingPackage ? "bg-gray-100 text-gray-600 hover:bg-gray-200" : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200"
                          }`} data-testid="toggle-edit-package">
                          {editingPackage ? <><RotateCcw className="h-3 w-3" /> Cancel</> : <><Pencil className="h-3 w-3" /> Edit Package</>}
                        </button>
                      </div>
                    )}

                    {/* View mode */}
                    {!editingPackage && (
                      <>
                        {d.deal_package.summary && (
                          <div className="rounded-xl border border-cream-warm bg-cream p-4 text-sm text-[#333] leading-relaxed">{d.deal_package.summary}</div>
                        )}
                        {d.deal_package.strengths?.length > 0 && (
                          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                            <h4 className="text-xs uppercase tracking-wider text-emerald-600 mb-3">Strengths</h4>
                            {d.deal_package.strengths.map((s, i) => (
                              <div key={i} className="text-sm text-[#333] flex gap-2 mb-1.5"><CheckCircle className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" /> {typeof s === "string" ? s : s.point || JSON.stringify(s)}</div>
                            ))}
                          </div>
                        )}
                        {d.deal_package.risks?.length > 0 && (
                          <div className="rounded-xl border border-orange-200 bg-orange-50 p-4">
                            <h4 className="text-xs uppercase tracking-wider text-orange-600 mb-3">Risks</h4>
                            {d.deal_package.risks.map((r, i) => (
                              <div key={i} className="text-sm text-[#333] flex gap-2 mb-1.5"><AlertCircle className="h-4 w-4 text-orange-500 shrink-0 mt-0.5" /> {typeof r === "string" ? r : r.point || JSON.stringify(r)}</div>
                            ))}
                          </div>
                        )}
                        {d.deal_package.last_edited_by && (
                          <div className="text-[10px] text-[#bbb] text-right">
                            Last edited by {d.deal_package.last_edited_by} on {new Date(d.deal_package.last_edited_at).toLocaleDateString()}
                          </div>
                        )}
                      </>
                    )}

                    {/* Edit mode */}
                    {editingPackage && (
                      <>
                        <div className="rounded-xl border border-cream-warm bg-cream p-4">
                          <h4 className="text-xs uppercase tracking-wider text-[#999] mb-2">Deal Summary</h4>
                          <textarea value={packageForm.summary}
                            onChange={e => setPackageForm(p => ({ ...p, summary: e.target.value }))}
                            className="w-full rounded-lg border border-[#e0d8c8] bg-white p-3 text-sm text-[#333] focus:outline-none focus:border-emerald-400 min-h-[100px] resize-y"
                            placeholder="Write or refine the deal summary for lender presentation..."
                            data-testid="edit-package-summary" />
                        </div>

                        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                          <h4 className="text-xs uppercase tracking-wider text-emerald-600 mb-2">Strengths</h4>
                          {packageForm.strengths.map((s, i) => (
                            <div key={i} className="flex items-center gap-2 mb-2">
                              <input value={s} onChange={e => {
                                const arr = [...packageForm.strengths]; arr[i] = e.target.value;
                                setPackageForm(p => ({ ...p, strengths: arr }));
                              }} className="flex-1 rounded-lg border border-emerald-200 bg-white px-3 py-1.5 text-sm focus:outline-none focus:border-emerald-400"
                                data-testid={`edit-strength-${i}`} />
                              <button onClick={() => setPackageForm(p => ({ ...p, strengths: p.strengths.filter((_, j) => j !== i) }))}
                                className="text-red-400 hover:text-red-600 p-1"><X className="h-3.5 w-3.5" /></button>
                            </div>
                          ))}
                          <button onClick={() => setPackageForm(p => ({ ...p, strengths: [...p.strengths, ""] }))}
                            className="text-xs text-emerald-600 font-semibold hover:underline" data-testid="add-strength">+ Add Strength</button>
                        </div>

                        <div className="rounded-xl border border-orange-200 bg-orange-50 p-4">
                          <h4 className="text-xs uppercase tracking-wider text-orange-600 mb-2">Risks</h4>
                          {packageForm.risks.map((r, i) => (
                            <div key={i} className="flex items-center gap-2 mb-2">
                              <input value={r} onChange={e => {
                                const arr = [...packageForm.risks]; arr[i] = e.target.value;
                                setPackageForm(p => ({ ...p, risks: arr }));
                              }} className="flex-1 rounded-lg border border-orange-200 bg-white px-3 py-1.5 text-sm focus:outline-none focus:border-orange-400"
                                data-testid={`edit-risk-${i}`} />
                              <button onClick={() => setPackageForm(p => ({ ...p, risks: p.risks.filter((_, j) => j !== i) }))}
                                className="text-red-400 hover:text-red-600 p-1"><X className="h-3.5 w-3.5" /></button>
                            </div>
                          ))}
                          <button onClick={() => setPackageForm(p => ({ ...p, risks: [...p.risks, ""] }))}
                            className="text-xs text-orange-600 font-semibold hover:underline" data-testid="add-risk">+ Add Risk</button>
                        </div>

                        <div className="flex justify-end">
                          <button onClick={saveDealPackage} disabled={savingPackage}
                            className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-xs font-semibold bg-emerald-700 text-white hover:bg-emerald-600 disabled:opacity-50 transition-colors"
                            data-testid="save-deal-package">
                            {savingPackage ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                            Save Deal Package
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Term Sheets Tab — Full Comparison with Platform Terms Flow */}
                {tab === "offers" && (
                  <div data-testid="detail-offers">
                    <TermSheetComparison leadId={leadId} onRefresh={fetchDeal} />
                  </div>
                )}

                {/* Photos Tab */}
                {tab === "photos" && (
                  <div className="space-y-5" data-testid="detail-photos">
                    {isOwner && (
                      <div className="rounded-xl border-2 border-dashed border-cream-warm bg-cream p-6 text-center">
                        <Camera className="h-8 w-8 text-[#999] mx-auto mb-3" />
                        <p className="text-sm text-[#555] mb-3">Upload property photos for lenders to review the condition and scope of work</p>
                        <input ref={fileRef} type="file" accept="image/*" multiple onChange={handlePhotoUpload} className="hidden" data-testid="photo-upload-input" />
                        <GoldButton onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="photo-upload-btn">
                          {uploading ? <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Uploading...</span> : <span className="flex items-center gap-2"><Upload className="h-4 w-4" /> Upload Photos</span>}
                        </GoldButton>
                      </div>
                    )}
                    {d.photos?.length > 0 ? (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {d.photos.map(p => (
                          <div key={p.doc_id} className="rounded-xl border border-cream-warm overflow-hidden bg-cream aspect-[4/3]">
                            <img src={`${API}/documents/download/${p.doc_id}?auth=${localStorage.getItem("ic_token")}`} alt={p.filename}
                              className="w-full h-full object-cover hover:scale-105 transition-transform" data-testid={`photo-${p.doc_id}`} />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-sm text-[#999]">
                        <Image className="h-10 w-10 text-[#ccc] mx-auto mb-2" />
                        No property photos uploaded yet
                      </div>
                    )}
                  </div>
                )}

                {/* Documents Tab */}
                {tab === "documents" && (
                  <div className="space-y-3" data-testid="detail-documents">
                    {d.documents?.map(doc => (
                      <a key={doc.doc_id} href={`${API}/documents/download/${doc.doc_id}?auth=${localStorage.getItem("ic_token")}`} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-3 rounded-xl border border-cream-warm bg-cream p-3.5 hover:border-gold/30 transition-colors"
                        data-testid={`doc-download-${doc.doc_id}`}>
                        <FileText className="h-5 w-5 text-gold-dark shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-[#0A0A0A] truncate">{doc.filename}</div>
                          <div className="text-xs text-[#888]">{doc.doc_type} &bull; {(doc.size / 1024).toFixed(0)} KB</div>
                        </div>
                        <Download className="h-4 w-4 text-[#999]" />
                      </a>
                    ))}
                    {(!d.documents || d.documents.length === 0) && (
                      <div className="text-center py-8 text-sm text-[#999]">No documents uploaded</div>
                    )}
                  </div>
                )}

                {/* Fee Breakdown Tab (Admin/Internal) */}
                {tab === "fees" && (isAdmin || ["originations", "underwriter", "processor", "lender_relations"].includes(viewerRole)) && d.fees && (
                  <div className="space-y-5" data-testid="detail-fees">
                    {d.fees.funded ? (
                      <>
                        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-center gap-3">
                          <CheckCircle className="h-6 w-6 text-emerald-500" />
                          <div>
                            <div className="text-lg font-bold text-emerald-700">Deal Funded</div>
                            <div className="text-xs text-emerald-600">{d.fees.lender_name && `Lender: ${d.fees.lender_name} — `}Funded {d.fees.funded_at ? new Date(d.fees.funded_at).toLocaleDateString() : ""}</div>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <FeeCard label="Loan Amount" value={fmtUSD(d.fees.loan_amount)} />
                          <FeeCard label="Total Revenue" value={fmtUSD(d.fees.total_revenue)} highlight />
                          <FeeCard label={`Origination Fee (${d.fees.origination_fee_pct}%)`} value={fmtUSD(d.fees.origination_fee)} />
                          <FeeCard label={`Platform Fee (${d.fees.platform_fee_pct}%)`} value={fmtUSD(d.fees.platform_fee)} />
                          <FeeCard label={`Lender Fee (${d.fees.lender_fee_pct}%)`} value={fmtUSD(d.fees.lender_fee)} />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-center gap-3">
                          <Clock className="h-6 w-6 text-amber-500" />
                          <div className="flex-1">
                            <div className="text-lg font-bold text-amber-700">Projected Revenue</div>
                            <div className="text-xs text-amber-600">Based on estimated loan of {fmtUSD(d.fees.estimated_loan)}</div>
                          </div>
                          {isAdmin && !editingFees && (
                            <button onClick={() => { setFeeForm({ origination_fee_pct: d.fees.origination_fee_pct || 2, platform_fee_pct: d.fees.platform_fee_pct || 1 }); setEditingFees(true); }}
                              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white border border-amber-300 text-amber-700 hover:bg-amber-100 transition-colors"
                              data-testid="edit-fees-btn">Edit Fees</button>
                          )}
                        </div>
                        {editingFees ? (
                          <div className="rounded-xl border border-cream-warm bg-cream p-4 space-y-3" data-testid="fee-edit-form">
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="text-xs font-bold text-[#555] block mb-1">Origination Fee %</label>
                                <input type="number" step="0.1" min="0" max="10" value={feeForm.origination_fee_pct}
                                  onChange={e => setFeeForm(f => ({ ...f, origination_fee_pct: parseFloat(e.target.value) }))}
                                  className="w-full px-3 py-2 rounded-lg border border-cream-warm text-sm focus:ring-1 focus:ring-emerald-400 focus:outline-none"
                                  data-testid="origination-pct-input" />
                              </div>
                              <div>
                                <label className="text-xs font-bold text-[#555] block mb-1">Platform Fee %</label>
                                <input type="number" step="0.1" min="0" max="10" value={feeForm.platform_fee_pct}
                                  onChange={e => setFeeForm(f => ({ ...f, platform_fee_pct: parseFloat(e.target.value) }))}
                                  className="w-full px-3 py-2 rounded-lg border border-cream-warm text-sm focus:ring-1 focus:ring-emerald-400 focus:outline-none"
                                  data-testid="platform-pct-input" />
                              </div>
                            </div>
                            <div className="rounded-lg bg-white border border-cream-warm p-3">
                              <div className="text-xs text-[#999] mb-1">Projected with new rates:</div>
                              <div className="grid grid-cols-3 gap-2 text-sm">
                                <div><span className="text-[#888]">Origination:</span> <span className="font-bold">{fmtUSD((d.fees.estimated_loan || 0) * (feeForm.origination_fee_pct || 0) / 100)}</span></div>
                                <div><span className="text-[#888]">Platform:</span> <span className="font-bold">{fmtUSD((d.fees.estimated_loan || 0) * (feeForm.platform_fee_pct || 0) / 100)}</span></div>
                                <div><span className="text-[#888]">Total:</span> <span className="font-bold text-emerald-600">{fmtUSD((d.fees.estimated_loan || 0) * ((feeForm.origination_fee_pct || 0) + (feeForm.platform_fee_pct || 0)) / 100)}</span></div>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button onClick={async () => {
                                setSavingFees(true);
                                try {
                                  await axios.put(`${API}/deals/${leadId}/fees`, feeForm);
                                  const r = await axios.get(`${API}/deals/${leadId}/detail`);
                                  setDetail(r.data);
                                  setEditingFees(false);
                                } catch (err) { console.error(err); }
                                finally { setSavingFees(false); }
                              }} disabled={savingFees}
                                className="px-4 py-2 rounded-lg text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-500 transition-colors disabled:opacity-50"
                                data-testid="save-fees-btn">{savingFees ? "Saving..." : "Save Fees"}</button>
                              <button onClick={() => setEditingFees(false)}
                                className="px-4 py-2 rounded-lg text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors">Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 gap-3">
                            <FeeCard label="Estimated Loan" value={fmtUSD(d.fees.estimated_loan)} />
                            <FeeCard label="Projected Revenue" value={fmtUSD(d.fees.projected_revenue)} highlight />
                            <FeeCard label={`Origination Fee (${d.fees.origination_fee_pct || 2}%)`} value={fmtUSD(d.fees.projected_origination)} />
                            <FeeCard label={`Platform Fee (${d.fees.platform_fee_pct || 1}%)`} value={fmtUSD(d.fees.projected_platform)} />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Messages Tab */}
                {tab === "messages" && (
                  <div data-testid="detail-messages">
                    <DealRoomChat leadId={leadId} />
                  </div>
                )}

                {/* Conditions Tab */}
                {tab === "conditions" && (
                  <ConditionsPanel dealId={leadId} />
                )}

                {/* Approval Tab */}
                {tab === "approval" && (
                  <ApprovalsPanel dealId={leadId} />
                )}

                {/* Timeline Tab */}
                {tab === "timeline" && (
                  <div data-testid="detail-timeline">
                    <TimelinePanel dealId={leadId} />
                  </div>
                )}

                {/* Reports Tab */}
                {tab === "reports" && (
                  <div className="space-y-4" data-testid="detail-reports">
                    <p className="text-sm text-[#666]">Download professionally formatted PDF reports for this deal.</p>
                    <div className="grid grid-cols-2 gap-3">
                      <button onClick={() => downloadReport(d.lead_id, "summary")}
                        className="flex items-center gap-3 p-4 rounded-xl border border-cream-warm bg-cream hover:border-emerald-300 transition-colors text-left"
                        data-testid="detail-download-summary">
                        <FileText className="h-5 w-5 text-emerald-600" />
                        <div>
                          <div className="text-sm font-semibold text-[#0A0A0A]">Deal Summary</div>
                          <div className="text-[10px] text-[#888]">Property, financials, borrower info</div>
                        </div>
                        <Download className="h-4 w-4 ml-auto text-[#888]" />
                      </button>
                      <button onClick={() => downloadReport(d.lead_id, "underwriting")}
                        className="flex items-center gap-3 p-4 rounded-xl border border-cream-warm bg-cream hover:border-emerald-300 transition-colors text-left"
                        data-testid="detail-download-underwriting">
                        <ShieldCheck className="h-5 w-5 text-blue-600" />
                        <div>
                          <div className="text-sm font-semibold text-[#0A0A0A]">Underwriting Report</div>
                          <div className="text-[10px] text-[#888]">Risk analysis, LTV, lender offers</div>
                        </div>
                        <Download className="h-4 w-4 ml-auto text-[#888]" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Lender Outreach Workflow (unified: Package + Send + Track) */}
                {tab === "lender_workflow" && (
                  <div data-testid="lender-workflow-tab">
                    <LenderOutreachWorkflow leadId={leadId} dealNumber={d.deal_number} />
                  </div>
                )}
                {tab === "closing" && (
                  <div data-testid="closing-tab">
                    <ClosingChecklist leadId={leadId} token={localStorage.getItem("ic_token")} userRole={viewerRole} dealNumber={d.deal_number} />
                  </div>
                )}
              </div>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function Row({ label, value }) {
  const isEmpty = value === null || value === undefined || value === '' || value === '-' || value === 'null' || value === 'N/A' || value === 'n/a' || value === 0;
  return (
    <div className="flex justify-between">
      <span className="text-[#888]">{label}</span>
      {isEmpty ? (
        <span className="text-[#bbb] italic text-right text-xs" data-testid={`not-provided-${label.toLowerCase().replace(/\s+/g, '-')}`}>Not provided</span>
      ) : (
        <span className="text-[#0A0A0A] font-medium text-right">{value}</span>
      )}
    </div>
  );
}

function FeeCard({ label, value, highlight }) {
  return (
    <div className={`rounded-xl border p-3.5 text-center ${highlight ? "border-emerald-200 bg-emerald-50" : "border-cream-warm bg-cream"}`}>
      <div className="text-[10px] uppercase tracking-wider text-[#999] mb-1">{label}</div>
      <div className={`text-xl font-bold ${highlight ? "text-emerald-700" : "text-[#0A0A0A]"}`}>{value}</div>
    </div>
  );
}


function DealVisibilityControl({ leadId, initialVisibility }) {
  const [visibility, setVisibility] = useState(initialVisibility || "open_market");
  const [saving, setSaving] = useState(false);

  const handleChange = async (newVis) => {
    setSaving(true);
    try {
      await axios.put(`${API}/loan-box/admin/deal/${leadId}/visibility?visibility=${newVis}`);
      setVisibility(newVis);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const visOptions = [
    { key: "open_market", label: "Open Market", color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" },
    { key: "preferred", label: "Preferred", color: "bg-blue-500/20 text-blue-300 border-blue-500/30" },
    { key: "exclusive", label: "Exclusive", color: "bg-gold/20 text-gold border-gold/30" },
  ];

  return (
    <div className="flex items-center gap-2 mt-3" data-testid="visibility-control">
      <span className="text-[10px] uppercase tracking-wider text-white/50 font-semibold">Visibility:</span>
      {visOptions.map(v => (
        <button key={v.key} onClick={() => handleChange(v.key)} disabled={saving}
          data-testid={`vis-${v.key}`}
          className={`px-2.5 py-1 rounded-full text-[10px] font-bold border transition-all ${visibility === v.key ? v.color : "bg-white/5 text-white/40 border-white/10 hover:border-white/30"}`}>
          {v.label}
        </button>
      ))}
      {saving && <Loader2 className="h-3 w-3 animate-spin text-white/50" />}
    </div>
  );
}
